import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './GanttDiagram.css';

// Hilfsfunktion: Zufälligen Monat und Jahr generieren
function getRandomMonthYear() {
  const year = new Date().getFullYear();
  const month = Math.floor(Math.random() * 12); // 0=Jan, 11=Dez
  return { month, year };
}

// Hilfsfunktion: Anzahl Tage im Monat
function getDaysInMonth(month, year) {
  return new Date(year, month + 1, 0).getDate();
}

// Aufgaben generieren, max. 3 ohne Vorgänger
function generateGanttTasks(numTasks = 10) {
  const tasks = [];
  let numWithoutPredecessor = 0;
  for (let i = 0; i < numTasks; i++) {
    const id = (i + 1).toString();
    const name = `Aufgabe ${id}`;
    const dauer = Math.floor(Math.random() * 7) + 2; // 2-8 Arbeitstage
    let vorgaenger = [];
    if (i === 0) {
      numWithoutPredecessor++;
    } else {
      if (numWithoutPredecessor < 3 && Math.random() < 0.3) {
        numWithoutPredecessor++;
      } else {
        const numPrevs = Math.floor(Math.random() * Math.min(3, i)) + 1; // 1-3 Vorgänger
        const prevIds = Array.from({ length: i }, (_, idx) => (idx + 1).toString());
        vorgaenger = prevIds.sort(() => 0.5 - Math.random()).slice(0, numPrevs);
      }
    }
    tasks.push({ id, name, dauer, vorgaenger });
  }
  return tasks;
}

export default function GanttDiagram() {
  const numTasks = 10;

  // Zeitachse und Matrix vorbereiten
  const { month: startMonth, year } = React.useMemo(() => getRandomMonthYear(), []);
  const totalDays = React.useMemo(() => Math.floor(Math.random() * 11) + 30, []); // 30-40 Tage
  const weekdayNames = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
  const monthNames = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
  const firstWeekdayIdx = React.useMemo(() => Math.floor(Math.random() * 7), []);

  // Erzeuge alle Tage mit Zusatzinfos
  let days = [];
  let month = startMonth;
  let dayCounter = 1;
  let daysLeft = totalDays;
  let absDayIdx = 0;
  while (daysLeft > 0) {
    const daysInThisMonth = getDaysInMonth(month, year);
    for (let d = dayCounter; d <= daysInThisMonth && daysLeft > 0; d++) {
      const weekdayIdx = (firstWeekdayIdx + absDayIdx) % 7;
      days.push({
        idx: absDayIdx,
        day: d,
        month,
        year,
        weekdayIdx,
        weekday: weekdayNames[weekdayIdx],
        isWorkday: weekdayIdx < 5
      });
      absDayIdx++;
      daysLeft--;
    }
    month = (month + 1) % 12;
    dayCounter = 1;
  }

  // Array aller Arbeitstage (Indexe in days)
  const workdayIndexes = days.filter(d => d.isWorkday).map(d => d.idx);

  // Matrix-Logik: Aufgaben werden auf Arbeitstage verteilt, Abhängigkeiten werden geprüft
  function assignTasksToMatrix(tasks, days, workdayIndexes) {
    const matrix = Array(tasks.length).fill(0).map(() => Array(days.length).fill(false));
    const assigned = [];
    const startDays = {}; // task.id -> Start-Index (im days-Array)
    const endDays = {};   // task.id -> End-Index (im days-Array)
    for (let i = 0; i < tasks.length; i++) {
      const task = tasks[i];
      let startIdx = null;
      let maxDauer = task.dauer;
      if (!task.vorgaenger || task.vorgaenger.length === 0) {
        // Aufgaben ohne Vorgänger: zufällig auf einen Arbeitstag in den ersten 5 Arbeitstage
        const maxStart = Math.min(5, workdayIndexes.length - 1);
        const workdayStartIdx = Math.floor(Math.random() * (maxStart + 1));
        startIdx = workdayIndexes[workdayStartIdx];
        // Prüfe, ob noch genug Platz für die volle Dauer ist, sonst kürzen
        const maxEndIdx = workdayIndexes.length - 1;
        const maxDauerAbHier = maxEndIdx - workdayStartIdx + 1;
        if (maxDauerAbHier < maxDauer) maxDauer = maxDauerAbHier;
      } else {
        // Aufgaben mit Vorgängern: nach dem spätesten Ende aller Vorgänger
        const latestPredecessorEnd = Math.max(...task.vorgaenger.map(pid => endDays[pid]));
        // Finde im workdayIndexes den ersten Index > latestPredecessorEnd
        let workdayStartIdx = workdayIndexes.findIndex(idx => idx > latestPredecessorEnd);
        // Prüfe, ob ab diesem Start noch genug Platz ist
        if (workdayStartIdx === -1 || workdayStartIdx + maxDauer > workdayIndexes.length) {
          // Verschiebe so weit wie möglich nach links, aber nicht vor latestPredecessorEnd
          workdayStartIdx = Math.max(workdayIndexes.length - maxDauer, 0);
          // Wenn das vor dem Vorgänger-Ende liegt, dann kürzen
          if (workdayIndexes[workdayStartIdx] <= latestPredecessorEnd) {
            workdayStartIdx = workdayIndexes.findIndex(idx => idx > latestPredecessorEnd);
            if (workdayStartIdx === -1) {
              // Es gibt keinen Platz mehr nach dem Vorgänger, Aufgabe passt nicht mehr rein
              continue;
            }
            // Kürze die Dauer, damit sie noch reinpasst
            maxDauer = workdayIndexes.length - workdayStartIdx;
          }
        }
        startIdx = workdayIndexes[workdayStartIdx];
        // Prüfe, ob noch genug Platz für die volle Dauer ist, sonst kürzen
        const maxEndIdx = workdayIndexes.length - 1;
        const maxDauerAbHier = maxEndIdx - workdayStartIdx + 1;
        if (maxDauerAbHier < maxDauer) maxDauer = maxDauerAbHier;
      }
      // Belege die Matrix für diese Aufgabe
      let count = 0;
      let lastIdx = startIdx;
      let belegteTage = [];
      for (let d = startIdx; d < days.length && count < maxDauer; d++) {
        if (days[d].isWorkday) {
          matrix[i][d] = true;
          lastIdx = d;
          belegteTage.push(d);
          count++;
        }
      }
      startDays[task.id] = startIdx;
      endDays[task.id] = lastIdx;
      assigned.push({ ...task, startDay: startIdx + 1, belegteTage, dauer: maxDauer });
    }
    return { assigned, matrix };
  }

  // Aufgaben persistent im localStorage speichern
  const [tasksWithMatrix] = React.useState(() => {
    const saved = localStorage.getItem('gantt_quiz');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {}
    }
    const tasks = generateGanttTasks(numTasks);
    const { assigned, matrix } = assignTasksToMatrix(tasks, days, workdayIndexes);
    localStorage.setItem('gantt_quiz', JSON.stringify({ assigned, matrix }));
    return { assigned, matrix };
  });

  React.useEffect(() => {
    return () => {
      localStorage.removeItem('gantt_quiz');
    };
  }, []);

  const { assigned: tasksWithStart, matrix: ganttMatrix } = tasksWithMatrix;

  // Dynamische Breite für Aufgaben-Spalte (max 25%)
  const taskColWidth = `${Math.min(25, 100 * (days.length + 1))}%`;

  // State für User-Auswahl (Set für schnelle Prüfung)
  const [selected, setSelected] = React.useState(() => new Set());
  const [result, setResult] = React.useState(null);
  const [showResult, setShowResult] = React.useState(false);

  function handleCellClick(taskIdx, dayIdx) {
    if (showResult) return;
    const key = `${taskIdx}-${dayIdx}`;
    setSelected(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  }

  function handleSubmit() {
    let correct = 0;
    let total = 0;
    tasksWithStart.forEach((task, tIdx) => {
      for (let d = 0; d < days.length; d++) {
        if (ganttMatrix[tIdx][d]) {
          total++;
          if (selected.has(`${tIdx}-${d}`)) correct++;
        }
      }
    });
    let falsePositives = 0;
    selected.forEach(key => {
      const [tIdx, dIdx] = key.split('-').map(Number);
      if (!ganttMatrix[tIdx] || !ganttMatrix[tIdx][dIdx]) falsePositives++;
    });
    setResult({ correct, total, falsePositives });
    setShowResult(true);
  }

  function getCellClass(rowIdx, colIdx) {
    const key = `${rowIdx}-${colIdx}`;
    const isSelected = selected.has(key);
    const isCorrect = ganttMatrix[rowIdx][colIdx];
    const isWeekend = !days[colIdx].isWorkday;
    if (isWeekend) return 'gantt-grid-cell gantt-grid-weekend';
    if (!showResult) {
      return 'gantt-grid-cell gantt-grid-checkbox' + (isSelected ? ' gantt-grid-selected-light' : '');
    } else {
      if (isSelected && isCorrect) return 'gantt-grid-cell gantt-grid-checkbox gantt-grid-correct';
      if (isSelected && !isCorrect) return 'gantt-grid-cell gantt-grid-checkbox gantt-grid-false';
      if (!isSelected && isCorrect) return 'gantt-grid-cell gantt-grid-checkbox gantt-grid-missed';
      return 'gantt-grid-cell gantt-grid-checkbox';
    }
  }

  function getStartInfo(task) {
    if (!task.vorgaenger || task.vorgaenger.length === 0) {
      const start = days[task.startDay - 1];
      if (start) {
        return `Start: ${start.day}. ${monthNames[start.month]}`;
      }
    }
    return '-';
  }

  function getActualDuration(task) {
    return task.belegteTage.length;
  }

  const navigate = useNavigate();
  const { moduleSlug } = useParams();

  return (
    <div className="questions-container">
      <h3>Aufgaben</h3>
      <table className="gantt-table">
        <thead>
          <tr>
            <th>Vorgang</th>
            <th>Beschreibung</th>
            <th>Dauer (Arbeitstage)</th>
            <th>Vorgänger</th>
            <th>Start (falls kein Vorgänger)</th>
          </tr>
        </thead>
        <tbody>
          {tasksWithStart.map((task, idx) => (
            <tr key={task.id}>
              <td>{task.id}</td>
              <td>{task.name}</td>
              <td>{getActualDuration(task)} Tage</td>
              <td>{task.vorgaenger.length > 0 ? task.vorgaenger.join(', ') : '-'}</td>
              <td>{getStartInfo(task)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <h3 style={{marginTop:'48px'}}>Gantt-Diagramm (Grid)</h3>
      <div style={{overflowX:'auto'}}>
        <table className="gantt-grid-table">
          <thead>
            <tr>
              <th style={{minWidth:100, width: taskColWidth, maxWidth: taskColWidth}}></th>
              {days.map((d, idx) => (
                <th key={"wd"+idx} className="gantt-grid-weekday-header">{d.weekday}</th>
              ))}
            </tr>
            <tr>
              <th style={{minWidth:100, width: taskColWidth, maxWidth: taskColWidth}}></th>
              {days.map((d, idx) => (
                <th key={idx} className="gantt-grid-day-header">{d.day}</th>
              ))}
            </tr>
            <tr>
              <th style={{minWidth:100, width: taskColWidth, maxWidth: taskColWidth}}></th>
              {days.map((d, idx) => {
                const isFirstOfMonth = idx === 0 || days[idx-1].month !== d.month;
                return isFirstOfMonth ? (
                  <th key={"m"+idx} colSpan={days.filter(dd=>dd.month===d.month).length} className="gantt-grid-month-header">
                    {monthNames[d.month]} {d.year}
                  </th>
                ) : null;
              })}
            </tr>
          </thead>
          <tbody>
            {tasksWithStart.map((task, rowIdx) => (
              <tr key={task.id}>
                <td className="gantt-grid-task-label" style={{minWidth:100, width: taskColWidth, maxWidth: taskColWidth}}>{task.name}</td>
                {days.map((d, colIdx) => (
                  <td
                    key={colIdx}
                    className={getCellClass(rowIdx, colIdx)}
                    onClick={() => handleCellClick(rowIdx, colIdx)}
                    tabIndex={0}
                    aria-label={`Tag ${d.day} für ${task.name}`}
                    role="button"
                  ></td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{marginTop: '32px', textAlign: 'center'}}>
        {!showResult ? (
          <button className="gantt-quiz-submit" onClick={handleSubmit}>Quiz abschließen</button>
        ) : (
          <button className="gantt-quiz-submit" onClick={() => navigate(`/learning/${moduleSlug}/topics`)}>Zurück</button>
        )}
      </div>
    </div>
  );
} 